import { EditActions, RPCAction } from 'lib/actions/rpc'
import { CancelFunc, emptyCancel } from 'lib/cancel'
import { NotifyClient } from 'lib/notify'
import { ListReturn, Status } from 'lib/rpc'
import { ActionStatus, Collector, expandValue, expandValueList, unaryInvoke, unaryListCollect } from 'lib/rpcts'
import { App } from 'pbts/c1/api/app/v1/app'
import {
    Connector,
    ConnectorConfigFieldActivity,
    ConnectorCredential,
    ConnectorExpandMask, ConnectorServiceDeleteRequest,
    ConnectorServiceGetRequest,
    ConnectorServiceGetResponse,
    ConnectorServiceListCredentialsRequest,
    ConnectorServiceListCredentialsResponse,
    ConnectorServiceListRequest,
    ConnectorServiceListResponse,
    ConnectorServiceRevokeCredentialRequest,
    ConnectorServiceRevokeCredentialResponse,
    ConnectorServiceRotateCredentialRequest,
    ConnectorServiceRotateCredentialResponse,
    ConnectorServiceUpdateDelegatedRequest,
    ConnectorServiceUpdateRequest,
    ConnectorServiceUpdateResponse,
    ConnectorServiceUploadResponse,
    ConnectorView,
    CreateAuthorizeURLRequest,
    CreateAuthorizeURLResponse, FileConfig,
    GetConnectorConfigFieldActivityRequest,
    GetConnectorConfigFieldActivityResponse,
} from 'pbts/c1/api/app/v1/connector'
import { ConnectorServiceClient } from 'pbts/c1/api/app/v1/connector.client'
import { File as fsFile, FileRefDatasource, FileRefID } from 'pbts/c1/api/filestore/v1/filestore'
import { DataSourceInstallation } from 'pbts/c1/api/integration/datasource/v1/catalog'
import { User } from 'pbts/c1/api/user/v1/user'
import { ConnectorCapabilities } from 'pbts/c1/connector/v2/connector'
import { Any } from 'pbts/google/protobuf/any'
import { Empty } from 'pbts/google/protobuf/empty'
import { Timestamp } from 'pbts/google/protobuf/timestamp'
import { Dispatch } from 'react'

export enum AppConnectorAction {
    create = 'AppConnectorAction.create',
    get = 'AppConnectorAction.get',
    list = 'AppConnectorAction.list',
    upload = 'AppConnectorAction.upload',
    delete = 'AppConnectorAction.delete',
    update = 'AppConnectorAction.update',
    updateDelegated = 'AppConnectorAction.updateDelegated',
    createAuthorizeURL = 'AppConnectorAction.createAuthorizeURL',
    getConnectorConfigActivity = 'AppConnectorAction.getConnectorConfigActivity',
    createFile = 'AppConnectorAction.createFile',
    updateFile = 'AppConnectorAction.updateFile',
    listCredentials = 'AppConnectorAction.listCredentials',
    rotateCredential = 'AppConnectorAction.rotateCredential',
    revokeCredential = 'AppConnectorAction.revokeCredential',
}

export type CreateAction = {
    type: AppConnectorAction.create
    payload: ActionStatus<ConnectorValue>
}

export type UploadAction = {
    type: AppConnectorAction.upload
    payload: ActionStatus<ConnectorServiceUploadResponse>
}

export type GetAction = {
    type: AppConnectorAction.get
    payload: ActionStatus<ConnectorValue>
}

export type ListAction = {
    type: AppConnectorAction.list
    payload: ListReturn<ConnectorValue>
}

export type DeleteAction = {
    type: AppConnectorAction.delete
    payload: ActionStatus<Empty>
}

export type UpdateAction = {
    type: AppConnectorAction.update
    payload: ActionStatus<ConnectorValue>
}

export type UpdateDelegatedAction = {
    type: AppConnectorAction.updateDelegated
    payload: ActionStatus<ConnectorValue>
}

export type CreateAuthorizeURLAction = {
    type: AppConnectorAction.createAuthorizeURL
    payload: ActionStatus<string>
}

export type GetConnectorConfigAction = {
    type: AppConnectorAction.getConnectorConfigActivity
    payload: ActionStatus<ConnectorConfigFieldActivity>
}

export type CreateFileAction = {
    type: AppConnectorAction.createFile
    payload: ActionStatus<ConnectorValue>
}

export type UpdateFileAction = {
    type: AppConnectorAction.updateFile
    payload: ActionStatus<ConnectorValue>
}

export type ListCredentialsAction = {
    type: AppConnectorAction.listCredentials
    payload: ListReturn<ConnectorCredential>
}

export type RotateCredentialsAction = {
    type: AppConnectorAction.rotateCredential
    payload: ActionStatus<ConnectorServiceRotateCredentialResponse>
}

export type RevokeCredentialsAction = {
    type: AppConnectorAction.revokeCredential
    payload: ActionStatus<ConnectorServiceRevokeCredentialResponse>
}

export type AppConnectorActions =
    | CreateAction
    | GetAction
    | UploadAction
    | ListAction
    | DeleteAction
    | UpdateAction
    | UpdateDelegatedAction
    | CreateAuthorizeURLAction
    | GetConnectorConfigAction
    | CreateFileAction
    | UpdateFileAction
    | ListCredentialsAction
    | RotateCredentialsAction
    | RevokeCredentialsAction

export const createDataSourceConfig = (dataSource: DataSourceInstallation, fileName: string): Any => {
    const file = fsFile.create({
        name: fileName,
        fileRef: {
            oneofKind: 'datasource',
            datasource: FileRefDatasource.create({ datasourceId: dataSource.id, fileName }),
        },
    })

    const conf = FileConfig.create({
        file,
    })
    return Any.pack(conf, FileConfig)
}

  /*
   * Note: Despite the name "createFileConfig", this function is used for both creating new
   * file connectors and updating existing ones. When updating, pass existingConfig to preserve
   * columnMap and profileAttributeMap (custom mappings). Without it, those mappings will be lost.
   */
export const createFileConfig = (files: File[], existingConfig?: FileConfig): [Any, File] => {
    if (!files || files.length !== 1) {
        const conf = FileConfig.create()
        return [Any.pack(conf, FileConfig), new File([], '')]
    }

    const f = files[0]
    const file = fsFile.create({
        name: f.name,
        type: f.type,
        size: f.size,
        lastModified: Timestamp.fromDate(new Date(f.lastModified)),
        fileRef: {
            oneofKind: 'id',
            id: FileRefID.create({}),
        },
    })

    const conf = FileConfig.create({
        // Preserve existing config (columnMap, profileAttributeMap, etc.)
        ...existingConfig,
        // Override with new file
        file,
    })
    return [Any.pack(conf, FileConfig), f]
}

export interface ConnectorValue extends Connector {
    app?: App
    users?: User[]
    capabilities?: ConnectorCapabilities
}

export const expandConnectorViewPayload = (msg: ConnectorView | undefined, values: Any[]): ConnectorValue => {
    return {
        ...Connector.create(msg?.connector ?? {}),
        app: expandValue<App>(msg?.appPath, App, values),
        users: expandValueList<User>(msg?.usersPath, User, values),
        capabilities: expandValue<ConnectorCapabilities>(msg?.capabilitiesPath, ConnectorCapabilities, values),
    }
}

export const expandConnectorView = (
    payload: ActionStatus<ConnectorServiceUpdateResponse>,
): ActionStatus<ConnectorValue> => {
    switch (payload.status) {
        case Status.OK:
            return {
                status: Status.OK,
                item: expandConnectorViewPayload(payload.item.connectorView, payload.item.expanded),
            }
        default:
            return payload
    }
}

export class ConnectorCollector extends Collector<ConnectorValue> {
    push(resp: ConnectorServiceListResponse): void {
        this.buf = this.buf.concat(
            resp.list.map((v): ConnectorValue => {
                return expandConnectorViewPayload(v, resp.expanded)
            }),
        )
    }
}

export class ConnectorCredentialsCollector extends Collector<ConnectorCredential> {
    push(resp: ConnectorServiceListCredentialsResponse): void {
        this.buf = this.buf.concat(resp.list)
    }
}

export const listConnectors = (
    dispatch: Dispatch<ListAction>,
    client: ConnectorServiceClient | undefined,
    appId: string,
    expandMask?: ConnectorExpandMask,
    notifyClient?: NotifyClient,
): (() => void) | undefined => {
    if (!client) {
        return emptyCancel
    }
    const collector = new ConnectorCollector()
    const req = ConnectorServiceListRequest.create({
        appId,
        expandMask,
    })
    return unaryListCollect<ConnectorServiceListRequest, ConnectorServiceListResponse>(
        req,
        client.list.bind(client),
        collector,
        (payload) => {
            switch (payload.status) {
                case Status.OK:
                    dispatch({
                        type: AppConnectorAction.list,
                        payload: {
                            status: Status.OK,
                            items: collector.buffer(),
                        },
                    })
                    return
                default:
                    dispatch({
                        type: AppConnectorAction.list,
                        payload,
                    })
            }
        },
        notifyClient,
    )
}

export const updateConnectorField = <T>(
    dispatch: Dispatch<UpdateAction>,
    dispatchField: Dispatch<EditActions>,
    makeRequest: (value: T) => ConnectorServiceUpdateRequest | void,
    client?: ConnectorServiceClient,
): ((value: T) => Promise<void>) => {
    const doEdit = async (value: T): Promise<void> => {
        if (!client) {
            return
        }
        dispatchField({
            type: RPCAction.edit,
            payload: {
                status: Status.LOADING,
            },
        })

        const req = makeRequest(value)
        if (!req) {
            return
        }

        unaryInvoke<ConnectorServiceUpdateRequest, ConnectorServiceUpdateResponse>(
            req,
            client.update.bind(client),
            (payload) => {
                dispatch({
                    type: AppConnectorAction.update,
                    payload: expandConnectorView(payload),
                })
                dispatchField({
                    type: RPCAction.edit,
                    payload,
                })
            },
        )
    }

    return async (value: T): Promise<void> => {
        return doEdit(value)
    }
}

export const updateConnector = (
    dispatch: Dispatch<UpdateAction>,
    client: ConnectorServiceClient | undefined,
    makeRequest: () => ConnectorServiceUpdateRequest | undefined,
    dispatchField?: Dispatch<EditActions>,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }
    if (dispatchField) {
        dispatchField({
            type: RPCAction.edit,
            payload: {
                status: Status.LOADING,
            },
        })
    }

    const req = makeRequest()
    if (req === undefined) {
        return emptyCancel
    }

    return unaryInvoke<ConnectorServiceUpdateRequest, ConnectorServiceUpdateResponse>(
        req,
        client.update.bind(client),
        (payload) => {
            if (payload.status === Status.OK) {
                dispatch({
                    type: AppConnectorAction.update,
                    payload: expandConnectorView(payload),
                })
                if (dispatchField) {
                    dispatchField({
                        type: RPCAction.edit,
                        payload,
                    })
                }
            } else {
                dispatch({
                    type: AppConnectorAction.update,
                    payload,
                })
            }
        },
    )
}

export const updateDelegatedConnector = (
    dispatch: Dispatch<UpdateDelegatedAction>,
    client: ConnectorServiceClient | undefined,
    makeRequest: () => ConnectorServiceUpdateDelegatedRequest | undefined,
    dispatchField?: Dispatch<EditActions>,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }
    if (dispatchField) {
        dispatchField({
            type: RPCAction.edit,
            payload: {
                status: Status.LOADING,
            },
        })
    }

    const req = makeRequest()
    if (req === undefined) {
        return emptyCancel
    }

    return unaryInvoke<ConnectorServiceUpdateDelegatedRequest, ConnectorServiceUpdateResponse>(
        req,
        client.updateDelegated.bind(client),
        (payload) => {
            if (payload.status === Status.OK) {
                dispatch({
                    type: AppConnectorAction.updateDelegated,
                    payload: expandConnectorView(payload),
                })
                if (dispatchField) {
                    dispatchField({
                        type: RPCAction.edit,
                        payload,
                    })
                }
            } else {
                dispatch({
                    type: AppConnectorAction.updateDelegated,
                    payload,
                })
            }
        },
    )
}

export const getConnector = (
    dispatch: Dispatch<GetAction>,
    client: ConnectorServiceClient | undefined,
    appId: string,
    connectorId: string,
    notifyClient?: NotifyClient,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }
    const req = ConnectorServiceGetRequest.create({
        appId,
        id: connectorId,
        expandMask: { paths: ['*'] },
    })
    return unaryInvoke<ConnectorServiceGetRequest, ConnectorServiceGetResponse>(
        req,
        client.get.bind(client),
        (payload) => {
            dispatch({
                type: AppConnectorAction.get,
                payload: expandConnectorPayload(payload),
            })
        },
        notifyClient,
    )
}

export interface ConnectorViewResponse {
    connectorView?: ConnectorView
    expanded: Any[]
}

export const expandConnectorPayload = (payload: ActionStatus<ConnectorViewResponse>): ActionStatus<ConnectorValue> => {
    switch (payload.status) {
        case Status.OK:
            return {
                status: Status.OK,
                item: expandConnectorViewPayload(payload.item.connectorView, payload.item.expanded),
            }
        default:
            return payload
    }
}

export const deleteConnector = (
    dispatch: Dispatch<DeleteAction>,
    client: ConnectorServiceClient | undefined,
    appId: string,
    connectorId: string,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }
    const req = ConnectorServiceDeleteRequest.create({
        appId,
        id: connectorId,
    })

    return unaryInvoke<ConnectorServiceDeleteRequest, Empty>(req, client.delete.bind(client), (payload) => {
        dispatch({
            type: AppConnectorAction.delete,
            payload,
        })
    })
}

export const createAuthorizeURL = (
    dispatch: Dispatch<CreateAuthorizeURLAction>,
    client: ConnectorServiceClient | undefined,
    appId: string,
    connectorId: string,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }

    return unaryInvoke<CreateAuthorizeURLRequest, CreateAuthorizeURLResponse>(
        CreateAuthorizeURLRequest.create({ appId, connectorId }),
        client.createAuthorizeURL.bind(client),
        (payload) => {
            if (payload.status === Status.OK) {
                dispatch({
                    type: AppConnectorAction.createAuthorizeURL,
                    payload: {
                        status: Status.OK,
                        item: payload.item.authorizationUrl,
                    },
                })
                return
            }
            dispatch({
                type: AppConnectorAction.createAuthorizeURL,
                payload,
            })
        },
    )
}

export const getConnectorConfigActivity = (
    dispatch: Dispatch<GetConnectorConfigAction>,
    client: ConnectorServiceClient | undefined,
    appId: string,
    connectorId: string,
    fieldName: string,
    notifyClient?: NotifyClient,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }

    return unaryInvoke<GetConnectorConfigFieldActivityRequest, GetConnectorConfigFieldActivityResponse>(
        GetConnectorConfigFieldActivityRequest.create({ appId, connectorId, fieldName }),
        client.getConnectorConfigFieldActivity.bind(client),
        (payload) => {
            if (payload.status === Status.OK) {
                dispatch({
                    type: AppConnectorAction.getConnectorConfigActivity,
                    payload: {
                        status: Status.OK,
                        item: payload.item.item || ConnectorConfigFieldActivity.create(),
                    },
                })
                return
            }
            dispatch({
                type: AppConnectorAction.getConnectorConfigActivity,
                payload,
            })
        },
        notifyClient,
    )
}

export const rotateConnectorCredential = (
    dispatch: Dispatch<RotateCredentialsAction>,
    client: ConnectorServiceClient | undefined,
    appId: string,
    connectorId: string,
    cb: (clientId: string, secret: string, name: string) => void,
): CancelFunc => (!client
    ? emptyCancel
    : unaryInvoke<ConnectorServiceRotateCredentialRequest, ConnectorServiceRotateCredentialResponse>(
        ConnectorServiceRotateCredentialRequest.create({ appId, connectorId }),
        client.rotateCredential.bind(client),
        (payload) => {
            dispatch({ type: AppConnectorAction.rotateCredential, payload })
            if ('item' in payload) {
                // assume the id exists :-/
                cb(
                    payload.item.credential?.clientId as string,
                    payload.item.credential?.clientSecret as string,
                    payload.item.credential?.displayName || '',
                )
            }
        },
    ))

export const revokeConnectorCredential = (
    dispatch: Dispatch<RevokeCredentialsAction>,
    client: ConnectorServiceClient | undefined,
    appId: string,
    connectorId: string,
    id: string,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }
    return unaryInvoke<ConnectorServiceRevokeCredentialRequest, ConnectorServiceRevokeCredentialResponse>(
        ConnectorServiceRevokeCredentialRequest.create({ appId, connectorId, id }),
        client.revokeCredential.bind(client),
        (payload) => {
            dispatch({ type: AppConnectorAction.revokeCredential, payload })
        },
    )
}

export const listConnectorCredentials = (
    dispatch: Dispatch<ListCredentialsAction>,
    client: ConnectorServiceClient | undefined,
    appId: string,
    connectorId: string,
    notifyClient?: NotifyClient,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }
    const collector = new ConnectorCredentialsCollector()
    const req = ConnectorServiceListCredentialsRequest.create({ appId, connectorId })
    return unaryListCollect<ConnectorServiceListCredentialsRequest, ConnectorServiceListCredentialsResponse>(
        req,
        client.listCredentials.bind(client),
        collector,
        (payload) => {
            switch (payload.status) {
                case Status.OK:
                    dispatch({
                        type: AppConnectorAction.listCredentials,
                        payload: { status: Status.OK, items: collector.buffer() },
                    })
                    return
                default:
                    dispatch({
                        type: AppConnectorAction.listCredentials,
                        payload,
                    })
            }
        },
        notifyClient,
    )
}
